import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { fmtDate, can } from "@/lib/helpers";
import { PageHeader, StatusBadge, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const empty = { name: "", code: "", invoice_prefix: "", gstin: "", address: "", phone: "", state_code: "27" };

export default function Branches() {
  const { user, refreshBranches, activeBranch, selectBranch } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const canCreate = can(user, "branches", "create");
  const canEdit = can(user, "branches", "edit");
  const canDelete = can(user, "branches", "delete");

  const load = async () => setRows((await api.get("/branches")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ ...empty, ...b }); setEditId(b.id); setOpen(true); };

  const submit = async () => {
    try {
      if (editId) {
        const { id, created_at, active, ...payload } = form;
        await api.put(`/branches/${editId}`, payload);
        toast.success("Branch updated");
      } else {
        await api.post("/branches", form);
        toast.success("Branch created");
      }
      setOpen(false); setForm(empty); setEditId(null); load(); refreshBranches();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (b) => {
    let usage = {};
    try { usage = (await api.get(`/branches/${b.id}/usage`)).data; } catch (e) { toast.error(apiError(e)); return; }
    const summary = Object.entries(usage).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k.replace(/_/g, " ")}`).join(", ") || "no data";
    const typed = window.prompt(`PERMANENTLY delete "${b.name}" with ALL its users and data (${summary})?\nThis cannot be undone. Type the branch code "${b.code}" to confirm:`);
    if (typed === null) return;
    if (typed.trim() !== b.code) { toast.error("Branch code did not match — nothing deleted"); return; }
    try {
      await api.delete(`/branches/${b.id}`);
      toast.success("Branch and all its data deleted");
      const left = await refreshBranches();
      if (b.id === activeBranch && left[0]) { selectBranch(left[0].id); return; }
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Branch Management" subtitle="Multi-branch masters, invoice prefixes & GSTIN.">
        {canCreate && (
          <Button onClick={openNew} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="add-branch-btn"><Plus size={16} className="mr-1" /> New Branch</Button>
        )}
      </PageHeader>

      <Card className="bg-white border-[#E6E2D6] shadow-sm">
        {rows.length === 0 ? <EmptyState /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Prefix</TableHead>
                <TableHead>GSTIN</TableHead><TableHead>Phone</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((b) => (
                <TableRow key={b.id} data-testid={`branch-row-${b.code}`}>
                  <TableCell className="font-medium">{b.name}</TableCell>
                  <TableCell>{b.code}</TableCell>
                  <TableCell>{b.invoice_prefix}</TableCell>
                  <TableCell className="text-xs">{b.gstin || "-"}</TableCell>
                  <TableCell className="text-sm">{b.phone || "-"}</TableCell>
                  <TableCell><StatusBadge status={b.active ? "active" : "cancelled"} /></TableCell>
                  <TableCell className="flex gap-1">
                    {canEdit && <Button variant="ghost" size="icon" onClick={() => openEdit(b)} data-testid={`edit-branch-${b.code}`}><PencilSimple size={16} /></Button>}
                    {canDelete && <Button variant="ghost" size="icon" onClick={() => remove(b)} className="text-[#9D2A2A]" data-testid={`delete-branch-${b.code}`}><Trash size={16} /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit Branch" : "New Branch"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[["name", "Branch Name"], ["code", "Code (unique)"], ["invoice_prefix", "Invoice Prefix"], ["gstin", "GSTIN"], ["phone", "Phone"], ["state_code", "State Code"]].map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs text-[#6B6B6B]">{l}</Label>
                <Input value={form[k] || ""} disabled={k === "code" && !!editId} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid={`branch-${k}`} />
              </div>
            ))}
            <div className="col-span-2">
              <Label className="text-xs text-[#6B6B6B]">Address</Label>
              <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="branch-address" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="branch-save">{editId ? "Save Changes" : "Create Branch"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
